import React, { useEffect, useState } from 'react'
import { Button, Input } from '@mobiscroll/react-latest'
import { debounce } from 'throttle-debounce'
import { ClipLoader } from 'react-spinners'
import RiderPortalMapView from 'src/components/riders/rider-portal/google/map/RiderPortalMapView'
import Modal from 'src/components/riders/rider-portal/common/Modal'
import { notification } from 'src/components/riders/rider-portal/common/Notification'
import Divider from 'src/components/ui/Divider'
import Cross from 'src/components/icons/ui/Cross'
import Search from 'src/components/icons/ui/Search'
import { googleMapService } from 'src/services/GoogleMap'
import { ILocationItemsProps, IMapModalProps } from 'src/types/rider-portal'
import { PORTAL_INPUT_PROPS, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

const LocationItems = ({ locations, handleSelectLocation }: ILocationItemsProps) => {
  return (
    <div className='absolute b-rad-8 bg-white rp-locations-wrapper rp-p16 top-50 z1'>
      {locations.map(location => (
        <p
          className='font-black3 rp-location-item rp-mb8 rp-mt0 text-sm'
          key={location.place_id}
          onClick={() => handleSelectLocation(location)}
        >
          {location.description}
        </p>
      ))}
    </div>
  )
}

const MapModal = ({
  center,
  dropOff,
  isOpen,
  loading,
  pickUp,
  serviceZones,
  title,
  handleCloseModal,
  handleMapModalValues,
  setLoading,
  updateLocation
}: IMapModalProps) => {
  const [locationQuery, setLocationQuery] = useState('')
  const [locationResult, setLocationResult] = useState<google.maps.places.AutocompletePrediction[]>([])

  const { common, placeholder } = RIDER_PORTAL_UI_MESSAGES

  useEffect(() => {
    if (isOpen) {
      const input = document.querySelector('.rp-search-location-input') as HTMLInputElement
      if (input) {
        setTimeout(() => input.focus(), 500)
      }
    }
  }, [isOpen])

  const handleSearchLocation = debounce(1000, async () => {
    if (locationQuery) {
      try {
        setLoading(true)
        const location = (await googleMapService.getPlacePredictions({
          input: locationQuery
        })) as google.maps.places.AutocompletePrediction[]
        if (location.length) {
          setLocationResult(location)
        }
      } catch (error) {
        console.log('Error: An error occurred when searching locations.')
      } finally {
        setLoading(false)
      }
    }
  })

  const handleSelectLocation = async (location: google.maps.places.AutocompletePrediction) => {
    const { description, place_id } = location
    try {
      setLoading(true)
      const placeDetails = (await googleMapService.getPlaceDetails(place_id)) as google.maps.places.PlaceResult
      if (placeDetails.geometry && placeDetails.geometry.location) {
        const { geometry, name } = placeDetails
        const location = {
          fullAddress: description,
          latitude: geometry.location.lat(),
          longitude: geometry.location.lng(),
          placeName: name || ''
        }
        setLocationQuery(description)
        setLocationResult([])
        handleMapModalValues(location)
      }
    } catch (error) {
      console.log('Error: An error occurred when fetching selected location details.')
    } finally {
      setLoading(false)
    }
  }

  const handleMarkerDragged = async (e: google.maps.MouseEvent, type: 'dropOff' | 'pickUp') => {
    const errMessage = 'Failed to retrieve a formatted address for the provided coordinates'
    const { latLng } = e
    const updatedCoordinates = {
      lat: latLng.lat(),
      lng: latLng.lng()
    }
    const addressName = await googleMapService
      .reverseGeocode(updatedCoordinates)
      .then((data: { formatted_address: string }) => {
        if (data.formatted_address) {
          return data.formatted_address
        }
        console.log(errMessage)
        return ''
      })
      .catch(e => {
        notification({ color: 'danger', message: errMessage })
        console.log(`${errMessage}: `, e)
        return ''
      })
    updateLocation({ fullAddress: addressName, latitude: latLng.lat(), longitude: latLng.lng(), placeName: '' }, type)
  }

  const handlePressEnter = (e: React.KeyboardEvent<HTMLButtonElement | HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchLocation()
    }
  }

  const handleOnClose = () => {
    setLocationQuery('')
    setLocationResult([])
    handleCloseModal()
  }

  return (
    <div className='rp-map-modal-container'>
      <Modal isOpen={isOpen} width={768} onClose={handleOnClose}>
        <>
          <div className='flex items-center justify-between rp-p16'>
            <h3 className='font-black1 font-medium my0 rp-pr24 text-lg'>{title}</h3>
            <Cross className='icon--18x18 svg-color-black1' onClick={handleOnClose} />
          </div>
          <Divider />
          <div className='rp-p16'>
            <div className='flex items-center relative rp-mb16'>
              <Input
                {...PORTAL_INPUT_PROPS}
                inputClass='rp-search-location-input'
                className='rp-input-with-end-btn rp-input-with-start-icon w100'
                disabled={loading}
                name='search-input'
                placeholder={placeholder.searchAddress}
                value={locationQuery || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationQuery(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handlePressEnter(e)}
              />
              <Search className='absolute icon--16x16 rp-ml12' />
              <Button
                className='bg-green1 rp-input-end-btn rp-search-location-btn'
                disabled={loading || !locationQuery}
                onClick={handleSearchLocation}
                onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => handlePressEnter(e)}
              >
                {loading ? <ClipLoader size={24} sizeUnit={'px'} color={'#405264'} loading={loading} /> : common.search}
              </Button>
              {locationResult.length > 1 && (
                <LocationItems locations={locationResult} handleSelectLocation={handleSelectLocation} />
              )}
            </div>
            <div className='rp-map-modal-map-view'>
              <RiderPortalMapView
                center={center}
                className='b-rad-8'
                dropOff={dropOff}
                isMarkerDraggable={true}
                pickUp={pickUp}
                serviceZones={serviceZones}
                showPolyline={true}
                handleMarkerDragged={handleMarkerDragged}
              />
            </div>
          </div>
        </>
      </Modal>
    </div>
  )
}

export default MapModal



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/MapModal.tsx